/**
 * Small presentational pieces for the TA Action Board view.
 *
 * Split out of TAActionBoardView so that file stays within the repo's 500-line limit.
 */
import { Mail, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Tabs, TabsList, TabsTrigger } from '@finn-auto/design-system/atoms/tabs';
import { useGmailConnection } from './GmailConnectionContext';
import { usePilotLock } from './PilotLock';
import { UNDO_DELAY_SECONDS, type PendingAction } from './usePendingActions';
import { fmtRelativeTime } from './format';
import { formatSendAt } from './scheduleReplyTime';

/**
 * A small "live" badge for a tab whose candidate-facing actions have gone live in
 * the staggered launch. Rendered only when the feature gate lists that feature,
 * so it is invisible until the launch is switched on (and everywhere the gate is
 * unset). Purely informational — the proxy is what actually enforces go-live.
 */
export function LiveChip() {
  return (
    <span
      className="ml-1.5 rounded-full bg-[#E6F4EA] px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-[#157F52]"
      title="Live — candidate actions in this tab are switched on"
    >
      live
    </span>
  );
}

/**
 * Shown while a confirmed archive or reply is counting down. Nothing has reached Lever, Make or a
 * candidate's inbox while this is on screen — Stop leaves everything exactly as it was.
 *
 * ── Why the button is `outlineWhite` and not `outline` ────────────────────────────────────────
 * This banner paints its own dark surface, and the design system's `outline` variant sets a border
 * and hover states but NO colour — so it inherited black text and a black border on near-black and
 * was, in Sofia's words, "completely black". A recruiter who did not already know Stop was there
 * could not find it, which also made the delay itself look like it did not exist (reported 13 Aug
 * 2026 as "there is no time lag on the archiving mails" — there is, and always was: the same
 * ten seconds, see useBoardActions.onArchive).
 *
 * `outlineWhite` states `border-white text-white` explicitly and inverts on hover. Note the trap
 * next door: `secondaryWhite` would give a white FILL but also sets no text colour, so the label
 * would inherit this banner's white and vanish the same way. On a surface that sets its own colour,
 * only a variant that sets BOTH is safe.
 */
export function PendingActionsBanner({
  pending,
  onCancel,
}: {
  pending: PendingAction[];
  onCancel: (id: string) => void;
}) {
  if (pending.length === 0) return null;
  // Bottom-centre and backdrop-free ON PURPOSE: the countdown exists so the
  // recruiter can carry on working, so it must never sit over the board.
  return (
    <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-col gap-2">
      {pending.map((action) => (
        <div
          key={action.id}
          className="min-w-[22rem] max-w-[34rem] overflow-hidden rounded-xl shadow-lg ring-1 ring-white/20"
          style={{ backgroundColor: '#1F1F1F', color: '#FFFFFF' }}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-4 px-4 py-3 text-sm">
            <span className="min-w-0">
              {action.label} in{' '}
              {/*
                aria-hidden on the ticking number: this is a polite live region, and a value that
                changes every second would have a screen reader re-announce the whole sentence ten
                times. The sentence around it is announced once, which is the useful part.
              */}
              <span className="font-semibold tabular-nums" aria-hidden="true">
                {action.secondsLeft}s
              </span>
              <span className="sr-only">a few seconds</span>. Nothing has happened yet.
            </span>
            <Button
              size="sm"
              variant="outlineWhite"
              className="shrink-0 font-semibold"
              onClick={() => onCancel(action.id)}
            >
              Stop
            </Button>
          </div>
          {/*
            The draining bar is the second half of the discoverability fix: a countdown you can SEE
            running says "this is your window to act" without anyone having to read the sentence.
          */}
          <div className="h-1 w-full bg-white/15" aria-hidden="true">
            <div
              className="h-full bg-white/70 transition-[width] duration-1000 ease-linear"
              style={{
                width: `${Math.max(0, Math.min(100, (action.secondsLeft / UNDO_DELAY_SECONDS) * 100))}%`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Board title and the Refresh control. */
export function BoardHeader({
  firstName,
  refreshedAt,
  cachedStale,
  loading,
  onRefresh,
}: {
  firstName: string;
  refreshedAt: Date | null;
  cachedStale: boolean | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="flex items-end justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">{firstName}'s board</h1>
        <p className="mt-1 text-sm text-gray-500">
          Synced from Lever · {refreshedAt ? fmtRelativeTime(refreshedAt) : '—'}
          {cachedStale && <span className="text-gray-400"> · saved copy</span>}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <GmailStatus />
        <Button size="sm" onClick={onRefresh} loading={loading} className="rounded-full px-4">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>
    </div>
  );
}

/**
 * The board's ONE Gmail connection, surfaced where every tab can see it.
 * Connecting here serves the Requests tab, panel suggestions AND the archive
 * flow (rejection emails become drafts in your own mailbox) in one grant.
 */
function GmailStatus() {
  const gmail = useGmailConnection();
  if (gmail.connected) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium"
        style={{ backgroundColor: '#DCF5E4', color: '#136C34' }}
        title="Gmail is connected for this session — the Requests tab reads your inbox, and archiving with an email puts the rejection draft in your mailbox."
      >
        <Mail className="h-3.5 w-3.5" /> Gmail connected
      </span>
    );
  }
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => void gmail.connect()}
      loading={gmail.connecting}
      className="rounded-full px-4"
      title="One Google grant for the whole board: candidate requests, panel suggestions, and rejection drafts in your own mailbox."
    >
      <Mail className="mr-1.5 h-3.5 w-3.5" /> Connect Gmail
    </Button>
  );
}

/**
 * FOUR top-level tabs. The three interview-scheduling sections (first round,
 * case, final) live inside 'scheduling' as sub-tabs — as five siblings they
 * overflowed a laptop's width, so the last tab could only be reached by
 * scrolling the strip sideways, which is where 🏁 Final rounds sat unseen.
 */
export type TabId = 'feedback' | 'scheduling' | 'sla' | 'requests';

/** The tab ids, and the guard the URL segment is validated against. */
export const TAB_IDS: TabId[] = ['feedback', 'scheduling', 'sla', 'requests'];
export const isTabId = (value: unknown): value is TabId =>
  typeof value === 'string' && (TAB_IDS as string[]).includes(value);

export interface BoardCounts {
  feedback: number;
  /** Candidates awaiting their first-round invitation (📨 sub-tab). */
  firstInterviews: number;
  /** Case-stage candidates without a panel (📅 sub-tab). */
  scheduling: number;
  finalRounds: number;
  sla: number;
  /**
   * null = not known yet, because the recruiter has not connected Gmail (or has not opened the
   * tab this session). Deliberately NOT rendered as 0: a zero badge reads as "nothing waiting
   * for you here", which is the one message that would stop anyone ever opening the tab.
   */
  requests: number | null;
}

/**
 * Boxed folder-tabs, matching the signed-off mockup: a hairline strip with the
 * current tab a snow-filled box (pearl border on three sides) sitting ON the
 * strip's bottom line, and a count PILL that turns trustedBlue when active.
 * `-mb-px` pulls each tab down 1px so the active box merges with the strip line;
 * `!` overrides the DS's own default fill/border. FINN tokens throughout
 * (#1A1A1A black, #707070 iron, #E9EAEC pearl, #F8F8F8 snow).
 */
const TAB_CLASS =
  'group -mb-px rounded-t-[2px] border border-solid border-transparent !border-b-0 ' +
  'px-4 py-[11px] text-sm font-semibold !bg-transparent text-[#707070] ' +
  'hover:text-[#1A1A1A] ' +
  'data-[state=active]:!bg-[#F8F8F8] data-[state=active]:!border-[#E9EAEC] ' +
  'data-[state=active]:!border-b-0 data-[state=active]:text-[#1A1A1A]';

/** The count pill inside a tab — pearl by default, trustedBlue when its tab is active. */
function TabCount({ n }: { n: number | string }) {
  return (
    <span
      className="ml-2 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[11px] font-semibold bg-[#E9EAEC] text-[#707070] group-data-[state=active]:bg-[#0072EA] group-data-[state=active]:text-white"
    >
      {n}
    </span>
  );
}

/**
 * The board's tab strip.
 *
 * Lives here rather than inline in TAActionBoardView because that file is up against the repo's
 * 500-line lint ceiling and every new tab would otherwise push it over.
 */
export function BoardTabs({
  tab,
  counts,
  onChange,
  finalRoundsUnavailable = false,
  showRequests = true,
}: {
  tab: TabId;
  counts: BoardCounts;
  onChange: (tab: TabId) => void;
  /** The proxy predates the 🏁 endpoints — the Final sub-tab shows a dash. */
  finalRoundsUnavailable?: boolean;
  /**
   * The Requests tab reads the signed-in recruiter's own mailbox, so it is only
   * shown on their OWN board — on a colleague's board it can never load their
   * requests and just reads as a dead tab. Hidden there.
   */
  showRequests?: boolean;
}) {
  // One number for the whole Scheduling tab: everything waiting to be scheduled
  // or invited, so the strip still says how much work is behind it. The split
  // per round is on the sub-tabs inside.
  const schedulingTotal =
    counts.firstInterviews + counts.scheduling + (finalRoundsUnavailable ? 0 : counts.finalRounds);
  // Staggered launch: chip the tabs whose candidate actions are switched on.
  // Scheduling is chipped at the sub-tab level (only Case is live), not here.
  const { featureLive } = usePilotLock();
  return (
    <Tabs value={tab} onValueChange={(v) => onChange(v as TabId)}>
      <TabsList className="w-full justify-start gap-0.5 border-b border-solid border-[#E9EAEC] px-0">
        <TabsTrigger value="feedback" className={TAB_CLASS}>
          Feedback <TabCount n={counts.feedback} />
        </TabsTrigger>
        <TabsTrigger value="scheduling" className={TAB_CLASS}>
          Scheduling <TabCount n={schedulingTotal} />
        </TabsTrigger>
        <TabsTrigger value="sla" className={TAB_CLASS}>
          Overdue <TabCount n={counts.sla} />
          {featureLive('overdue') && <LiveChip />}
        </TabsTrigger>
        {showRequests && (
          <TabsTrigger value="requests" className={TAB_CLASS}>
            Requests{' '}
            <span title={counts.requests === null ? 'Connect your Gmail to see candidate questions' : undefined}>
              <TabCount n={counts.requests === null ? '•' : counts.requests} />
            </span>
            {featureLive('requests') && <LiveChip />}
          </TabsTrigger>
        )}
      </TabsList>
    </Tabs>
  );
}

/**
 * A count in the NAV: the number in its status colour, with no pill around it.
 *
 * The filled badge is what made the tabs read as chips — the same rounded,
 * coloured rectangles the summary strip uses for data. The colour still carries
 * the signal (red for SLA, orange for scheduling); only the box is gone.
 */
export function CountText({ n, color }: { n: number; color: string }) {
  return (
    <span className="ml-1.5 text-xs font-bold" style={{ color }}>
      {n}
    </span>
  );
}

/** Filled badge — still used inside the tabs' CONTENT, where chips belong. */
export function CountBadge({ n, bg, text }: { n: number; bg: string; text: string }) {
  return (
    <span
      className="ml-1.5 inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-bold"
      style={{ backgroundColor: bg, color: text }}
    >
      {n}
    </span>
  );
}

/** Toast wording for a completed archive, including the partial-success case. */
/** Why an approved rejection email produced no Gmail draft, in the recruiter's terms. */
const SEND_FAILURE: Record<string, string> = {
  'gmail-not-connected': 'Google is not connected, so NOTHING was sent',
  'no-candidate-email': 'the candidate has no email address in Lever, so nothing was sent',
  'mailbox-mismatch': 'the connected Google account is not yours, so nothing was sent',
  'gmail-error': 'Gmail refused the message, so nothing was sent',
  'unresolved-booking-link':
    'the feedback-call booking link could not be resolved, so nothing was sent — set up the feedback call on the Interview Links page first',
};

export function archiveResultToast(
  result: {
    emailApproved?: boolean;
    emailSent?: boolean;
    scheduled?: boolean;
    scheduledFor?: string;
    to?: string;
    attachmentName?: string | null;
    reason?: string;
  },
  name: string,
  reason: string
) {
  // Scheduled, not sent-now: a success (the archive committed and the email is queued), so it must
  // come BEFORE the "approved but not sent" warning below — a queued email is exactly that shape.
  if (result.scheduled && result.scheduledFor) {
    return {
      status: 'success' as const,
      description: `${name} archived as "${reason}" — rejection email scheduled for ${formatSendAt(result.scheduledFor)}. Change or cancel it under Scheduled at the top of Feedback.`,
    };
  }
  // An approved email that did NOT go is a warning, never a quiet success: the
  // candidate hears nothing unless the recruiter acts on it, and by this point
  // the archive in Lever has already committed.
  if (result.emailApproved && !result.emailSent) {
    const why = SEND_FAILURE[result.reason || ''] || 'the email was not sent';
    // An unresolved booking link is not a "send it yourself" case: the server did
    // not persist the approved text on this path, and re-sending would carry the
    // placeholder. The `why` already says to set up the feedback call — no Log-page
    // promise. Every other reason keeps the recover-from-the-Log-page hint.
    const recovery =
      result.reason === 'unresolved-booking-link'
        ? ''
        : ' The approved text is on the Log page; send it yourself.';
    return {
      status: 'warning' as const,
      description: `${name} archived — but ${why}.${recovery}`,
    };
  }
  return {
    status: 'success' as const,
    description: result.emailSent
      ? `${name} archived as "${reason}" — rejection email sent${result.to ? ` to ${result.to}` : ''}${
          result.attachmentName ? ` with ${result.attachmentName}` : ''
        }.`
      : `${name} archived as "${reason}".`,
  };
}
